import React, { useState, useEffect } from "react";
import { Dialog, IconButton } from "@mui/material";
import {
  X,
  User,
  FileText,
  Paperclip,
  Clock,
  MessageSquare,
  Download,
  AlertCircle,
} from "lucide-react";
import { ExplanationRequest } from "../../../types/explanations";
import { getSchoolClassLabel } from "../../common/utils";
import {
  downloadFile,
  getFileDetails,
} from "../../../server/Files CRUD/Requests";
import { GetFileDetailsOutput } from "../../../server/Files CRUD/OutputsTypes";

interface RequestDetailsModalProps {
  open: boolean;
  request: ExplanationRequest | any; // Accepter les deux types pour compatibilité
  onClose: () => void;
}

const RequestDetailsModal: React.FC<RequestDetailsModalProps> = ({
  open,
  request,
  onClose,
}) => {
  const [files, setFiles] = useState<GetFileDetailsOutput[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);

  useEffect(() => {
    if (open && request?.fileIds && request.fileIds.length > 0) {
      const fetchFiles = async () => {
        setFilesLoading(true);
        try {
          const filesData = await Promise.all(
            request.fileIds.map((fileId: string) => getFileDetails(fileId)),
          );

          const validFiles = filesData
            .filter((response: any) => response.status === 200)
            .map((response: any) => response.body as GetFileDetailsOutput);

          setFiles(validFiles);
        } catch (error) {
          console.error("Erreur lors de la récupération des fichiers:", error);
        } finally {
          setFilesLoading(false);
        }
      };

      fetchFiles();
    } else {
      setFiles([]);
    }
  }, [open, request?.fileIds]);

  // Formater la taille du fichier
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleFileDownload = async (fileId: string, filename: string) => {
    try {
      const blob = await downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors du téléchargement du fichier:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        zIndex: 9999,
        "& .MuiDialog-paper": {
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header moderne */}
      <div className="bg-[#E3B500] text-white">
        <div className="p-4 sm:p-6 lg:p-8 flex justify-between items-center">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold">
                Détails de la demande
              </h2>
              <p className="text-sm sm:text-base text-white/90 mt-1">
                Explication mathématique
              </p>
            </div>
          </div>
          <IconButton
            onClick={onClose}
            className="text-white hover:bg-white/20 transition-colors rounded-xl"
            sx={{
              width: { xs: 40, sm: 44 },
              height: { xs: 40, sm: 44 },
            }}
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </IconButton>
        </div>
      </div>

      {/* Contenu moderne */}
      <div className="bg-gray-50 min-h-[400px] max-h-[70vh] overflow-y-auto">
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="space-y-6">
            {/* Informations principales */}
            <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* Statut */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-[#E3B500]" />
                    <span className="text-sm font-medium text-gray-700">
                      Statut
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                    <Clock className="w-4 h-4" />
                    En attente de validation
                  </span>
                </div>

                {/* Classe */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Classe
                    </span>
                  </div>
                  <p className="text-base font-semibold text-gray-900">
                    {getSchoolClassLabel(request.schoolClass)}
                  </p>
                </div>
              </div>
            </div>

            {/* Informations de l'étudiant */}
            <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-[#E3B500]" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Informations de l'étudiant
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">
                    Email
                  </p>
                  <p className="text-sm text-gray-800">
                    {request.studentEmail || "Non disponible"}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 font-medium mb-1">Nom</p>
                  <p className="text-sm text-gray-800">
                    {request.student
                      ? `${request.student.firstname} ${request.student.lastname}`
                      : "Non disponible"}
                  </p>
                </div>
              </div>
            </div>

            {/* Description du problème */}
            <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-5 h-5 text-[#E3B500]" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Problème à résoudre
                </h3>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {request.description ||
                    request.comment ||
                    "Aucune description du problème"}
                </p>
              </div>
            </div>

            {/* Disponibilités de l'élève */}
            <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-[#E3B500]" />
                <h3 className="text-lg font-semibold text-gray-800">
                  Disponibilités de l'élève
                </h3>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {request.availability || "Non précisées"}
                </p>
              </div>
            </div>

            {/* Fichiers joints */}
            {request.fileIds && request.fileIds.length > 0 && (
              <div className="bg-white rounded-lg p-4 sm:p-6 border border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-[#E3B500]" />
                  <h3 className="text-lg font-semibold text-gray-800">
                    Fichiers joints
                  </h3>
                  <span className="bg-[#E3B500] text-white px-2 py-1 rounded-full text-xs font-medium">
                    {request.fileIds.length}
                  </span>
                </div>

                <div className="space-y-3">
                  {filesLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#E3B500] border-t-transparent"></div>
                      <span className="ml-2 text-sm text-gray-600">
                        Chargement...
                      </span>
                    </div>
                  ) : files && files.length > 0 ? (
                    files.map((file: GetFileDetailsOutput) => (
                      <div
                        key={file._id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p
                              className="text-sm font-medium text-gray-900 truncate"
                              title={file.originalName}
                            >
                              {file.originalName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {file.mimeType} • {formatFileSize(file.size || 0)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            handleFileDownload(file._id, file.originalName)
                          }
                          className="p-2 text-[#E3B500] hover:bg-[#E3B500]/10 rounded-lg transition-colors"
                          title="Télécharger"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Aucun fichier disponible
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer moderne */}
      <div className="bg-gray-100 border-t border-gray-200 p-4 sm:p-6">
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="group px-6 py-3 bg-[#E3B500] text-white rounded-xl flex items-center gap-2 hover:bg-[#CA8A04] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 font-medium text-sm sm:text-base"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 group-hover:rotate-90 transition-transform duration-200" />
            <span>Fermer</span>
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default RequestDetailsModal;
